import { Injectable } from '@angular/core';
import { TaxListInfoDataService } from '@products/services/tax-list-info-data.service';
import {
  TaxListInfoListing,
  TaxListInfoFilterType,
  TaxListInfoState,
  TaxListInfoSelectedFilters,
  TaxListInfoFundStatus,
  TaxListInfoToggleState,
  TaxListInfoAction,
  TaxInfoStateList,
  TaxInfoStateParam,
  TaxInfoStickySelectEvent,
  TaxInfoDownloadType,
  CheckboxSelectType,
  FILTER_DEFAULT_CLASS,
} from '@products/types/tax-list-info.type';
import { Logger } from '@utils/logger';
import { forkJoin, Observable, of, ReplaySubject, Subject, zip } from 'rxjs';
import taxListInfoQuery from '@graphql/tax-list-info/tax-list-info.graphql';
import taxListInfoStatesQuery from '@graphql/tax-list-info/tax-list-info-state.graphql';
import { FundId, FundIdentifier, FundShareClassId, SwitchState } from '@types';
import { TaxListInfo } from '@products/models/tax-list-info';
import { SiteConfigService } from '@services/site-config.service';
import cloneDeep from 'lodash/cloneDeep';
import sortedUniq from 'lodash/sortedUniq';
import { DropdownItem, InfoItem, PagedRecords } from '@frk/eds-components';
import { TranslateService } from '@shared/translate/translate.service';
import { EMDASH } from '@products/utils/constants/product.constants';
import { FavoritesService } from '@products/services/favorites.service';
import { ProfileService } from '@services';
import { switchMap, map } from 'rxjs/operators';
import dayjs from 'dayjs';
import {
  EMDASH_CELL,
  ExcelDownloadService,
  FtCellObject,
  getCellObject,
} from '@products/services/excel-download.service';
import { FundOverviewService } from '@products/services/fund-overview.service';
import { formatPercentage } from '@utils/text/number-utils';
import { Product } from '@models';

const logger = Logger.getLogger('TaxListInfoService');

// ********** Page configurations start **********

// Listing page size.
const PAGE_SIZE = 10000;

const TAX_DATA_POINTS: string[] = [
  'foreignSourceIncome',
  'foreignSourceQualifiedDividends',
  'directUSGovernmentObligations',
  'specifiedPvtActivityBondInterest',
  'dividendsReceivedDeduction',
  'section163',
  'indianaResidentTaxableIncome',
  'utahResidentTaxableIncome',
  'stateFlag',
];

enum ProductTypeOptions {
  'MUTUAL_FUNDS' = 'Mutual Funds',
  'EXCHANGE_TRADED_FUNDS' = 'ETFs',
  'SEPARATELY_MANAGED_ACCOUNTS' = 'Managed Accounts',
  'VARIABLE_INSURANCE_OPTIONS' = 'Variable Insurance',
  'INTERVAL_FUNDS' = 'Interval Funds',
  'MONEY_FUNDS' = 'Money Market Funds',
  '529_PORTFOLIOS' = '529 Portfolios',
  'CLOSED_END_FUNDS' = 'Closed-End Funds',
  'INSTITUTIONAL_FUNDS' = 'TIF',
}

// Searchable fields.
// Item name must be equal to "field name" in 'TaxListInfoListing'.
const SEARCHABLE_FIELDS: (keyof TaxListInfoListing)[] = [
  'fundId',
  'fundName',
  'shareClassName',
  'cusip',
  'ticker',
  'taId',
];

// ********** Page configurations ends **********

@Injectable({
  providedIn: 'root',
})
export class TaxListInfoService {
  private state: TaxListInfoState = {};
  private taxListInfoState$: ReplaySubject<TaxListInfoState>;
  protected pagedRecords: PagedRecords<TaxListInfoListing>;
  private taxListInfoListing: TaxListInfoListing[] = [];
  private favoritesToggleState: TaxListInfoToggleState = {
    showFavoritesOnlySwitch: false,
  };
  private favoritesToggleState$: Subject<TaxListInfoToggleState>;
  private selectedFilters: TaxListInfoSelectedFilters;
  private totalSelectedRows$: Subject<number>;
  constructor(
    private taxListInfoDataService: TaxListInfoDataService,
    private siteConfigService: SiteConfigService,
    private translateService: TranslateService,
    private profileService: ProfileService,
    private favoritesService: FavoritesService,
    private excelDownloadService: ExcelDownloadService,
    private fundOverviewService: FundOverviewService
  ) {
    this.taxListInfoState$ = new ReplaySubject();
    this.pagedRecords = new PagedRecords<TaxListInfoListing>(PAGE_SIZE);
    this.favoritesToggleState$ = new Subject<TaxListInfoToggleState>();
    this.totalSelectedRows$ = new Subject<number>();
  }

  /**
   * Return the state updates as an observable.
   */
  public getTaxListInfoState$(): Observable<TaxListInfoState> {
    return this.taxListInfoState$.asObservable();
  }

  /**
   * Fetch tax list info feed and format it into our own feasible data structure.
   * Extract filter items from the feed and configure pagination stuff.
   * Finally publish the state accordingly.
   */
  public populate(): void {
    this.taxListInfoDataService.register(taxListInfoQuery).subscribe(
      (taxListInfo) => {
        logger.debug('Tax List Info Feed: ', taxListInfo);

        // Process the pds feed as required.
        this.processState(taxListInfo);
      },
      (error) => {
        logger.error('Error in fetching tax list info feed from PDS: ', error);

        // Publish erroneous state.
        this.taxListInfoState$.error(error);
      }
    );
  }

  /**
   * Process the business logics required for tax list info listing.
   * Further, format the final listing to required data structure and extract the filters.
   * Finally publish the state accordingly.
   * @param taxListInfo tax list info full pds feed
   */
  private processState(taxListInfo: TaxListInfo[]): void {
    // Enclose all processing within error handling.
    try {
      // Step 1: Process all the grid related tasks,
      // Format the pds feed into feasible data structure.
      // Filter out funds based upon status.
      // Hence, remove "inactive funds", while other 'status' will be handled in 'template' accordingly.

      this.taxListInfoListing = this.formatTaxListInfoData(
        taxListInfo.filter(
          (taxListInfoData) =>
            taxListInfoData.fundStatus !== TaxListInfoFundStatus.inactive
        )
      );

      // Load the grid initially for funds against "primary share class".
      const primaryShareClassListing: TaxListInfoListing[] = this.taxListInfoListing.filter(
        (taxListInfoListingObj) => taxListInfoListingObj.isPrimaryShareClass
      );
      // Extract filter(s) data from the listing.
      this.state.filters = this.extractFiltersData(primaryShareClassListing);
      if (!this.state?.taxYear) {
        this.state.taxYear = this.taxListInfoListing[0]?.taxYear || '';
      }

      // Also, assign the record details.
      // Initially, 'total' & 'filtered' records would be same with default "page size".
      this.state.recordDetails = {
        total: primaryShareClassListing.length,
        filtered: primaryShareClassListing.length,
      };

      // Further, configure pagination to display listing in ag-grid table accordingly.
      this.pagedRecords.records = cloneDeep(primaryShareClassListing);

      // Step 5: Finally, publish the updated state.
      this.state.action = TaxListInfoAction.INITIAL_LOAD;
      this.state.isLoaded = true;
      this.taxListInfoState$.next(this.state);
    } catch (exception) {
      logger.error('Error in processing feed and assigning state: ', exception);

      // Publish incomplete state.
      this.state.isLoaded = false;
      this.taxListInfoState$.next(this.state);
    }
  }

  /**
   * Format the pds feed into feasible data structure.
   * And, sort the same alphabetically against fund name and share class name.
   * @param taxListInfoData tax list info feed
   * @returns sorted tax list info list
   */
  private formatTaxListInfoData(
    taxListInfoData: TaxListInfo[]
  ): TaxListInfoListing[] {
    return this.sortTaxListInfoListing(
      taxListInfoData.map((taxListInfoObj) => {
        // Extract default identifier key against taxonomy for current object.
        const defaultIdentifierKey = this.siteConfigService.getDefaultIdentifierKey(
          taxListInfoObj.productTaxonomy
        );

        // Search for 'key' as 'field' in current object, and retrieve its value.
        const defaultIdentifier: FundIdentifier = defaultIdentifierKey
          ? (taxListInfoObj[defaultIdentifierKey] as FundIdentifier)
          : null;

        // Assign and form the expected data structure.
        return {
          fundId: taxListInfoObj.fundId,
          fundName: taxListInfoObj.fundName,
          fundShareClassId: `${taxListInfoObj.fundId}-${taxListInfoObj.shareClassCode}` as FundShareClassId,
          shareClassCode: taxListInfoObj.shareClassCode,
          shareClassName: taxListInfoObj.shareClassName,
          productType: taxListInfoObj.productType,
          taxYear: taxListInfoObj.taxYear,
          cusip: taxListInfoObj.cusip,
          ticker: taxListInfoObj.ticker,
          investmentGroup: taxListInfoObj.investmentGroup,
          taId: taxListInfoObj.taId,
          assetClass: taxListInfoObj.assetClass,
          foreignSourceIncome: formatPercentage(
            taxListInfoObj.foreignSourceIncome
          ),
          foreignSourceQualifiedDividends: formatPercentage(
            taxListInfoObj.foreignSourceQualifiedDividends
          ),
          directUSGovernmentObligations: formatPercentage(
            taxListInfoObj.directUSGovernmentObligations
          ),
          specifiedPvtActivityBondInterest: formatPercentage(
            taxListInfoObj.specifiedPvtActivityBondInterest
          ),
          dividendsReceivedDeduction: formatPercentage(
            taxListInfoObj.dividendsReceivedDeduction
          ),
          section163: formatPercentage(taxListInfoObj.section163),
          indianaResidentTaxableIncome: formatPercentage(
            taxListInfoObj.indianaResidentTaxableIncome
          ),
          utahResidentTaxableIncome: formatPercentage(
            taxListInfoObj.utahResidentTaxableIncome
          ),
          stateFlag: taxListInfoObj.stateFlag,
          primaryShareClass: taxListInfoObj.primaryShareClass,
          productTaxonomy: taxListInfoObj.productTaxonomy,
          isPrimaryShareClass: taxListInfoObj.primaryShareClassIndicator,
          link: this.siteConfigService.getFundLink(
            taxListInfoObj.fundId,
            taxListInfoObj.shareClassCode,
            taxListInfoObj.fundName,
            defaultIdentifier
          ),
          isSoftLaunchShareClass: taxListInfoObj.isSoftLaunchShareClass,
        };
      })
    );
  }

  /**
   * Returns the sorted tax list info listing against fund name and share class name.
   * @param taxListInfoListing unformatted tax list info listing.
   */
  private sortTaxListInfoListing(
    taxListInfoListing: TaxListInfoListing[]
  ): TaxListInfoListing[] {
    return taxListInfoListing.sort((prev, next) => {
      const fundNameCompVal: number = prev.fundName.localeCompare(
        next.fundName
      );

      // If fund name seems to be same, further check against share class name.
      if (fundNameCompVal === 0) {
        return prev.shareClassName.localeCompare(next.shareClassName);
      }

      return fundNameCompVal;
    });
  }

  /**
   * Extract the filters data as needed.
   * @param taxListInfoListing formatted tax list info listing.
   * @param filterTypes filter type list
   * @returns list of filter type(s) with their item(s)
   */
  private extractFiltersData(
    taxListInfoListing: TaxListInfoListing[]
  ): TaxListInfoFilterType[] {
    const taxListInfoFilterType: TaxListInfoFilterType[] = [];

    // 1. Get all the filters from site config
    const filterTypes = this.siteConfigService.getTaxInfoFilterOptions();

    // 2. Loop through each filter type listed.
    if (filterTypes.length > 0) {
      filterTypes.forEach((filterType) => {
        // 3. Extract the unique values against the filter type above.
        //    Example, all product types values like, MF/ETF for the field 'productType'.
        //    So, first extract all values, sort them and then extract non-duplicate values.
        const filterTypeUniqueItems: string[] = sortedUniq(
          taxListInfoListing
            .map(
              (taxInfoListingObj) =>
                taxInfoListingObj[FILTER_DEFAULT_CLASS[filterType]]
            )
            // as part of WDE-3524 hiding the money funds from asset class filter
            .filter(
              (taxInfoObj: string) =>
                taxInfoObj?.toLowerCase() !== 'money funds'
            )
            .sort((prev: string, next: string) => prev.localeCompare(next))
        );

        // 4. Create the items list by mapping different required field values.
        const taxListInfoFilterTypeItems: DropdownItem[] = [];
        if (
          FILTER_DEFAULT_CLASS[filterType] === FILTER_DEFAULT_CLASS.PRODUCT_TYPE
        ) {
          const productTypeFilterOptions = this.siteConfigService.getTaxInfoProductTypeFilterOptions();
          if (productTypeFilterOptions.length > 0) {
            productTypeFilterOptions.forEach((filterOption: string) => {
              taxListInfoFilterTypeItems.push({
                label: this.translateService.instant(
                  `products.capgains-filter-producttype-${ProductTypeOptions[
                    filterOption
                  ]
                    ?.toLowerCase()
                    .split(' ')
                    .join('-')}`
                ),
                value: ProductTypeOptions[filterOption],
                checked: false,
              });
            });
          }
        } else {
          filterTypeUniqueItems.map((uniqueItem) => {
            if (uniqueItem) {
              taxListInfoFilterTypeItems.push({
                label: uniqueItem,
                value: uniqueItem,
                checked: false,
              });
            }
          });
        }

        // 5. Create the item list for tax data point filter
        if (
          FILTER_DEFAULT_CLASS[filterType] ===
          FILTER_DEFAULT_CLASS.TAX_DATAPOINT
        ) {
          TAX_DATA_POINTS.forEach((taxDataPoint: string) => {
            taxListInfoFilterTypeItems.push({
              label: this.translateService.instant(
                `products.us-tax-info-page-${taxDataPoint
                  .toLowerCase()
                  .split(' ')
                  .join('-')}`
              ),
              value: taxDataPoint,
              checked: false,
            });
          });
        }

        // 6. Finally, push the created filter type object to the list.
        taxListInfoFilterType.push({
          filterLabel: filterType, // Intentionally made lower case, to use it as filter label in kebab-case.
          filterName: FILTER_DEFAULT_CLASS[filterType],
          filterItems: taxListInfoFilterTypeItems,
        });
      });
    }
    return taxListInfoFilterType;
  }

  /**
   * ******************** Favorite toggle methods ********************
   */

  /**
   * Return the favorite toggle state updates as an observable.
   */
  public getFavoritesToggleState$(): Observable<TaxListInfoToggleState> {
    return this.favoritesToggleState$.asObservable();
  }

  /**
   * Handle favorite toggle visibility and its state.
   * Load favorite funds and save their info in original listing with matching fund share class id.
   */
  public handleFavoriteToggle(): void {
    // Invoke favorite list.
    this.favoritesService.loadFavorites();

    // Initialize favorites only state.
    // Check if user is logged in along with a truthy flag from BR config.
    this.profileService
      .isLoggedIn$()
      .pipe(
        switchMap((isLoggedIn) =>
          isLoggedIn && this.siteConfigService.isFavoriteFundsEnabled()
            ? this.getFavoriteList$()
            : of(null)
        )
      )
      .subscribe(
        (favoriteFundShareClassIds: FundShareClassId[]) => {
          if (favoriteFundShareClassIds) {
            if (favoriteFundShareClassIds.length) {
              logger.debug(
                'Favorite fund share class ids: ',
                favoriteFundShareClassIds
              );

              // Save the favorite fund info for respective record in original listing for any favorite 'FundShareClassId' found.
              favoriteFundShareClassIds.forEach((favoriteFundShareClassId) => {
                const matchedFundIndex: number = this.taxListInfoListing.findIndex(
                  (taxListInfoObj) =>
                    taxListInfoObj.fundShareClassId === favoriteFundShareClassId
                );

                if (matchedFundIndex !== -1) {
                  this.taxListInfoListing[matchedFundIndex].isFavorite = true;
                }
              });
            }

            // Assign the default values only on first valid load.
            if (!this.favoritesToggleState.favoritesOnlySwitchState) {
              this.favoritesToggleState.showFavoritesOnlySwitch = true;
              this.favoritesToggleState.favoritesOnlySwitchState =
                SwitchState.OFF;
            }

            // Also, trigger the grid filter listing, if toggle is in 'on' state.
            // This would be the case, when, user has made an action on toggle button and favorite list is loaded late.
            if (
              this.favoritesToggleState.favoritesOnlySwitchState ===
              SwitchState.ON
            ) {
              // Pass the 'selectedFilters' current state to apply it, if any, by the time favorite fund list is available.
              this.filterListing(this.selectedFilters, true);
            }
          }

          // Post update in original listing, if any, its ready for toggle action.
          // If not a valid share class id(s), load the default visibility as false.
          this.favoritesToggleState$.next(this.favoritesToggleState);
        },
        (error) => {
          logger.error('Error in loading favorites: ', error);
          this.favoritesToggleState$.next(this.favoritesToggleState);
        }
      );
  }

  /**
   * Return the favorite "fund share class ids" as an observable.
   */
  private getFavoriteList$(): Observable<FundShareClassId[]> {
    return this.favoritesService
      .getFavoritesOnLoad$()
      .pipe(() => this.favoritesService.getFavorites$());
  }

  /**
   * ******************** Grid filter method(s) ********************
   */

  /**
   * Filter the listing as per selected filters and update the subscriptions.
   * @param selectedFilters selected dropdown or searched text
   * @param isFavoritesToggleActive flag value specifying if favorite toggle has state 'on'
   */
  public filterListing(
    selectedFilters: TaxListInfoSelectedFilters,
    isFavoritesToggleActive: boolean
  ): void {
    const primaryShareClassListing: TaxListInfoListing[] = [];
    const nonPrimaryShareClassListing: TaxListInfoListing[] = [];
    let favoritesOnlyListing: TaxListInfoListing[] = [];
    let filteredListing: TaxListInfoListing[] = [];
    // Save the current toggle & filter(s) state as it is needed to persist the same,
    // When favorite fund list is available so late that user has already made action on toggle.
    this.favoritesToggleState.favoritesOnlySwitchState = isFavoritesToggleActive
      ? SwitchState.ON
      : SwitchState.OFF;
    // And, might have selected filter(s) too.
    this.selectedFilters = selectedFilters;

    // Check if favorite toggle state is 'on'.
    // If yes, make a separate listing with only favorite funds.
    // This will be used to have dropdown and search filters.
    if (isFavoritesToggleActive) {
      // Intentionally done cloneDeep to avoid any reference updates.
      favoritesOnlyListing = cloneDeep(
        this.taxListInfoListing.filter(
          (taxListInfo) =>
            taxListInfo.isFavorite && taxListInfo.isPrimaryShareClass
        )
      );
    } else {
      // Extract the current listing into primary and non-primary share class listing.
      this.taxListInfoListing.filter((taxListInfo) => {
        if (taxListInfo.isPrimaryShareClass) {
          primaryShareClassListing.push(taxListInfo);
        } else {
          nonPrimaryShareClassListing.push(taxListInfo);
        }
      });
    }

    // Iterate with selected dropdown 'items', and, use 'some' to return as 'true' as soon as any 'item' is found.
    const isAnyFilterDropdownItemSelected: boolean = selectedFilters?.selectedDropdowns.some(
      (selectedDropdown) =>
        selectedDropdown.filterItems.map(
          (filterItem) => filterItem.value as string
        ).length
    );

    // Check if any filter item or search text is present.
    // If yes, proceed with grid listing filter.
    // Else, return the 'primary share class' or 'favoritesOnly' listing.
    if (isAnyFilterDropdownItemSelected || selectedFilters?.searchedText) {
      // Filter the listing based upon dropdown(s) selected.
      if (isAnyFilterDropdownItemSelected) {
        filteredListing = isFavoritesToggleActive
          ? favoritesOnlyListing
          : primaryShareClassListing;
        selectedFilters.selectedDropdowns.forEach((selectedDropdown) => {
          const selectedDropdownItems: string[] = selectedDropdown.filterItems.map(
            (filterItem) => filterItem.value as string
          );
          if (selectedDropdownItems.length) {
            filteredListing = filteredListing.filter((taxListInfoObj) => {
              if (selectedDropdown.filterName === 'taxDataPoint') {
                return selectedDropdownItems.every((selectedTaxDataItem) => {
                  if (selectedTaxDataItem === 'stateFlag') {
                    return taxListInfoObj[selectedTaxDataItem];
                  }
                  return taxListInfoObj[selectedTaxDataItem] !== EMDASH;
                });
              } else {
                return selectedDropdownItems.includes(
                  taxListInfoObj[selectedDropdown.filterName]
                );
              }
            });
          }
        });
      } else {
        // If no dropdown filter exist, copy the "primary share class" or 'favoriteOnly' listing as the new filtered listing "till now".
        filteredListing = cloneDeep(
          isFavoritesToggleActive
            ? favoritesOnlyListing
            : primaryShareClassListing
        ); // Intentionally done cloneDeep to avoid any reference updates.
      }

      // Filter the new 'filteredListing' (and 'nonPrimaryShareClassListing' listing accordingly) based upon search input, if present.
      if (selectedFilters?.searchedText) {
        filteredListing = this.sortTaxListInfoListing(
          (isFavoritesToggleActive
            ? filteredListing
            : [...filteredListing, ...nonPrimaryShareClassListing]
          ).filter((taxListInfoObj) =>
            SEARCHABLE_FIELDS.some((field) =>
              this.isSearchTermMatched(
                taxListInfoObj,
                field,
                selectedFilters.searchedText.toLowerCase()
              )
            )
          )
        );
      }
    } else {
      filteredListing = cloneDeep(
        isFavoritesToggleActive
          ? favoritesOnlyListing
          : primaryShareClassListing
      ); // Intentionally done cloneDeep to avoid any reference updates.
    }

    // Update the filtered record length and action accordingly.
    this.state.action = TaxListInfoAction.FILTER_ROWS;
    this.state.recordDetails.filtered = filteredListing.length;

    // Publish the updated state.
    this.taxListInfoState$.next(this.state);

    // Also, publish the filtered listing to pagination library.
    // This will further update all relevant subscriptions.
    this.pagedRecords.records = filteredListing;
  }

  /**
   * Handle the conditional check of fields against the search term.
   * @param taxListInfoListing tax list info record
   * @param searchableField field to be searched in estimate record
   * @param searchedText text to be matched in the searched field
   * @returns boolean flag if search term is matched
   */
  private isSearchTermMatched(
    taxListInfoListing: TaxListInfoListing,
    searchableField: keyof TaxListInfoListing,
    searchedText: string
  ): boolean {
    switch (searchableField) {
      case 'fundId':
        return (
          (taxListInfoListing[searchableField] as string)
            .toLowerCase()
            .includes(searchedText) && taxListInfoListing.isPrimaryShareClass
        );
      case 'fundName':
        return (
          taxListInfoListing[searchableField]
            .toLowerCase()
            .includes(searchedText) && taxListInfoListing.isPrimaryShareClass
        );
      case 'cusip':
      case 'ticker':
      case 'taId':
        return taxListInfoListing[searchableField]
          .toLowerCase()
          .includes(searchedText);
      case 'shareClassName':
        return (
          taxListInfoListing[searchableField].toLowerCase() === searchedText
        );
      default:
        return (taxListInfoListing[searchableField] as string)
          .toLowerCase()
          .includes(searchedText);
    }
    return false;
  }

  /**
   * Return current page records as observable.
   */
  public getCurrentPage$(): Observable<TaxListInfoListing[]> {
    return this.pagedRecords.currentPage$;
  }

  /**
   * Handle download action events.
   * Process excel headers, body and caveats.
   * Finally export the grid data to excel.
   * @param downloadType type of download to be passed, to download selected option
   * @param fundId fund id to be passed, if download action corresponds to selected fund
   */
  public handleDownloadAction(
    downloadType: TaxInfoDownloadType,
    fundId?: FundId
  ): void {
    let stateDownloadableVariables: TaxInfoStateParam[] = [];
    let downloadableRecords: TaxListInfoListing[] = [];
    // Get all downloadable records based on from where download is triggered
    switch (downloadType) {
      case TaxInfoDownloadType.SELECTED: {
        downloadableRecords = this.taxListInfoListing.filter(
          (taxListInfo: TaxListInfoListing) =>
            taxListInfo.isPrimaryShareClass && taxListInfo.isChecked
        );
        break;
      }
      case TaxInfoDownloadType.FILTERED: {
        downloadableRecords = this.pagedRecords.records.filter(
          (taxListInfo: TaxListInfoListing) => taxListInfo.isPrimaryShareClass
        );
        break;
      }
      case TaxInfoDownloadType.SINGLE: {
        downloadableRecords = this.taxListInfoListing.filter(
          (taxListInfo: TaxListInfoListing) =>
            taxListInfo.isPrimaryShareClass && taxListInfo.fundId === fundId
        );
        break;
      }
      case TaxInfoDownloadType.FAVORITE: {
        downloadableRecords = this.taxListInfoListing.filter(
          (taxListInfo: TaxListInfoListing) =>
            taxListInfo.isPrimaryShareClass && taxListInfo.isFavorite
        );
        break;
      }
      default: {
        downloadableRecords = this.taxListInfoListing.filter(
          (taxListInfo: TaxListInfoListing) => taxListInfo.isPrimaryShareClass
        );
      }
    }

    // To store unique states
    const stateHeaderSet = new Set<string>();
    const downloadableStatesRecords: TaxListInfoListing[] = downloadableRecords.filter(
      (taxListInfo: TaxListInfoListing) =>
        taxListInfo.stateFlag && !taxListInfo.isTaxExemptAvailable
    );
    const downloadableStatesAvailableRecords: TaxListInfoListing[] = downloadableRecords.filter(
      (taxListInfo: TaxListInfoListing) =>
        taxListInfo.stateFlag && taxListInfo.isTaxExemptAvailable
    );
    downloadableStatesAvailableRecords.forEach(
      (taxInfo: TaxListInfoListing) => {
        taxInfo.taxExemptData?.forEach((state: InfoItem) =>
          stateHeaderSet.add(state.label)
        );
      }
    );
    if (downloadableStatesRecords) {
      stateDownloadableVariables = downloadableStatesRecords.map(
        (record: TaxListInfoListing) => {
          return {
            fundId: record.fundId,
            shareClassCode: record.shareClassCode,
            year: Number(record.taxYear),
          };
        }
      );
    }
    // Fetch all tax states for downloading funds
    this.fetchTaxInfoStates$(stateDownloadableVariables).subscribe(
      (taxInfoStates: TaxInfoStateList[]) => {
        let stateDataStartingColNumber = 0;
        const taxInfoStateInfoList = taxInfoStates.values()
          ? [...taxInfoStates.values()]
          : [];
        taxInfoStateInfoList.forEach((taxInfoState: TaxInfoStateList) => {
          taxInfoState.states.forEach((state: InfoItem) =>
            stateHeaderSet.add(state.label)
          );
        });
        // All States included into header
        const stateHeader = [...stateHeaderSet].sort();
        // Get fields required in excel
        const requiredDownloadableFields: FtCellObject[][] = downloadableRecords.map(
          (record: TaxListInfoListing) => {
            // Fill default values '—' for all states
            const defaultStatesValue: FtCellObject[] = Array(
              stateHeader.length
            ).fill(EMDASH_CELL);
            if (record.stateFlag) {
              const taxInfoState: TaxInfoStateList = record.isTaxExemptAvailable
                ? { fundId: record.fundId, states: record.taxExemptData }
                : taxInfoStateInfoList.find(
                    (state: TaxInfoStateList) => record.fundId === state.fundId
                  );
              // Updates values for all states
              this.updateStateValue(
                stateHeader,
                defaultStatesValue,
                taxInfoState
              );
            }

            return [
              getCellObject(record.fundName, 's'),
              getCellObject(
                formatPercentage(record.directUSGovernmentObligations),
                's'
              ),
              getCellObject(
                formatPercentage(record.dividendsReceivedDeduction),
                's'
              ),
              getCellObject(formatPercentage(record.foreignSourceIncome), 's'),
              getCellObject(
                formatPercentage(record.foreignSourceQualifiedDividends),
                's'
              ),
              getCellObject(
                formatPercentage(record.specifiedPvtActivityBondInterest),
                's'
              ),
              getCellObject(formatPercentage(record.section163), 's'),
              getCellObject(
                formatPercentage(record.indianaResidentTaxableIncome),
                's'
              ),
              getCellObject(
                formatPercentage(record.utahResidentTaxableIncome),
                's'
              ),
              ...defaultStatesValue,
            ];
          }
        );

        // Column Header Text
        const dataHeaders: FtCellObject[] = [
          getCellObject(
            this.translateService.instant('products.us-tax-info-page-fundname')
          ),
          getCellObject(
            this.translateService.instant(
              'products.us-tax-info-page-directusgovernmentobligations'
            )
          ),
          getCellObject(
            this.translateService.instant(
              'products.us-tax-info-page-dividendsreceiveddeduction'
            )
          ),
          getCellObject(
            this.translateService.instant(
              'products.us-tax-info-page-foreignsourceincome'
            )
          ),
          getCellObject(
            this.translateService.instant(
              'products.us-tax-info-page-foreignsourcequalifieddividends'
            )
          ),
          getCellObject(
            this.translateService.instant(
              'products.us-tax-info-page-specifiedpvtactivitybondinterest'
            )
          ),
          getCellObject(
            this.translateService.instant(
              'products.us-tax-info-page-section163'
            ),
            's'
          ),
          getCellObject(
            this.translateService.instant(
              'products.us-tax-info-page-indianaresidenttaxableincome'
            )
          ),
          getCellObject(
            this.translateService.instant(
              'products.us-tax-info-page-utahresidenttaxableincome'
            )
          ),
        ];
        if (stateHeader.length) {
          stateDataStartingColNumber = dataHeaders.length;
        }
        stateHeader.forEach((h: string) => dataHeaders.push(getCellObject(h)));

        // Add headers to fullData
        requiredDownloadableFields.unshift(dataHeaders);

        // Get Caveat and pass it to excel data
        forkJoin([
          this.excelDownloadService.getProximalCaveat$('USTaxInfoExcelBottom'),
          this.excelDownloadService.getProximalCaveat$('USTaxInfoExcelTop'),
        ]).subscribe((data) => {
          const [bottomCaveat, topCaveat]: [
            FtCellObject[][],
            FtCellObject[][]
          ] = data;
          this.exportToExcel(
            requiredDownloadableFields,
            topCaveat,
            bottomCaveat,
            stateDataStartingColNumber
          );
        });
      }
    );
  }

  /**
   * Updates default states value with actual values
   * @param stateHeader All states heared sorted array
   * @param defaultStatesValue default value '—' for all states, which will be replaced by actual value
   * @param taxInfoState actual states value
   */
  private updateStateValue(
    stateHeader: string[],
    defaultStatesValue: FtCellObject[],
    taxInfoState: TaxInfoStateList
  ): void {
    if (taxInfoState) {
      taxInfoState.states.forEach((state: InfoItem) => {
        defaultStatesValue[stateHeader.indexOf(state.label)] = getCellObject(
          state.val,
          's'
        );
      });
    }
  }

  /**
   * Export the table data to excel and auto download.
   * @param fullData full record for excel
   */
  private exportToExcel(
    fullData?: FtCellObject[][],
    topCaveat?: FtCellObject[][],
    bottomCaveat?: FtCellObject[][],
    stateDataStartingColNumber?: number
  ): void {
    import('xlsx').then((xlsx) => {
      // Create new worksheet with topCaveat
      const workSheet = xlsx.utils.aoa_to_sheet(topCaveat);
      const workBook = xlsx.utils.book_new();
      const fileName =
        this.translateService.instant('products.us-tax-info-excel-name') +
        '-' +
        dayjs().format('MMDDYYYY') +
        '.xlsx';
      // Feet to columns
      workSheet['!cols'] = this.fitToColumn(fullData);

      const stateSectionTitle = [
        this.translateService.instant('products.us-tax-info-page-stateflag'),
      ];
      // Append data
      if (stateDataStartingColNumber) {
        xlsx.utils.sheet_add_aoa(workSheet, [stateSectionTitle], {
          origin: { r: topCaveat.length, c: stateDataStartingColNumber },
        });
      }

      xlsx.utils.sheet_add_aoa(workSheet, fullData, {
        origin: 'A' + (topCaveat.length + 2),
      });

      // Add bottom caveat
      xlsx.utils.sheet_add_aoa(workSheet, bottomCaveat, {
        origin: 'A' + (topCaveat.length + 2 + fullData.length + 2),
      });

      xlsx.utils.book_append_sheet(workBook, workSheet);
      xlsx.writeFile(workBook, fileName);
    });
  }

  /**
   * Get maximum character of each column.
   * @param data full record for excel
   */
  private fitToColumn(data: FtCellObject[][]) {
    return data[0].map((a, i) => ({
      wch: Math.max(
        ...data.map((a2: FtCellObject[]) => {
          return a2[i]?.v?.toString().length;
        })
      ),
    }));
  }

  /**
   * Get all tax info stated for funds which has states
   * @param params array of parameters to invoke overview pds service
   * @returns observable of tax states values for all funds which has state flag True
   */
  private fetchTaxInfoStates$(
    params: TaxInfoStateParam[]
  ): Observable<TaxInfoStateList[]> {
    if (!Array.isArray(params) || params.length === 0) {
      return of([]);
    }
    return zip(
      ...params.map((param: TaxInfoStateParam) => {
        return this.fundOverviewService.register(taxListInfoStatesQuery, param);
      })
    ).pipe(
      map((taxStateProducts: Product[]) => {
        const taxInfoStates: TaxInfoStateList[] = [];
        taxStateProducts.forEach((taxStateProduct: Product) => {
          if (taxStateProduct?.shareClasses[0]) {
            taxInfoStates.push({
              fundId: taxStateProduct?.shareClasses[0]?.identifiers?.fundId,
              states: taxStateProduct?.shareClasses[0]?.tax?.states,
            });
          }
        });
        return taxInfoStates;
      })
    );
  }
  /**
   * Update selected rows, which will have all selected row's tax information
   * @param selectEvent event triggered by selecting/unselecting checkboxes
   */
  public updateSelection(selectEvent: TaxInfoStickySelectEvent): void {
    let checkedItems: TaxListInfoListing[] = [];
    if (selectEvent.itemsChanged === CheckboxSelectType.ALL) {
      checkedItems = this.taxListInfoListing.filter(
        (taxListInfo: TaxListInfoListing) =>
          this.pagedRecords.records.some(
            (taxListInfoPage: TaxListInfoListing) =>
              taxListInfo.fundId === taxListInfoPage.fundId &&
              taxListInfo.shareClassCode === taxListInfoPage.shareClassCode
          )
      );
      checkedItems.forEach(
        (taxListInfo: TaxListInfoListing) =>
          (taxListInfo.isChecked = selectEvent.isChecked)
      );
    } else if (selectEvent.itemsChanged === CheckboxSelectType.INDIVIDUAL) {
      const taxInfo: TaxListInfoListing = this.taxListInfoListing.find(
        (taxListInfo: TaxListInfoListing) =>
          taxListInfo.fundId === selectEvent.item.fundId &&
          taxListInfo.shareClassCode === selectEvent.item.shareClassCode
      );
      taxInfo.isChecked = selectEvent.isChecked;
      checkedItems = [taxInfo];
    }
    this.updateStateInfo$(checkedItems).subscribe((data: boolean) => {
      if (data) {
        this.totalSelectedRows$.next(
          this.taxListInfoListing.reduce(
            (n: number, taxListInfo: TaxListInfoListing) =>
              taxListInfo.isPrimaryShareClass && taxListInfo.isChecked
                ? n + 1
                : n,
            0
          )
        );
      }
    });
  }

  /**
   * Return the total checked items count as an observable.
   */
  public getTotalSelectedRows$(): Observable<number> {
    return this.totalSelectedRows$.asObservable();
  }

  /**
   * get the tax info for the tax exempt data
   */
  public getTaxInfoTaxExemptData$(
    fundDataServiceParams: TaxInfoStateParam
  ): Observable<Product> {
    return this.fundOverviewService.register(
      taxListInfoStatesQuery,
      fundDataServiceParams
    );
  }
  /**
   * get selected tax info
   * @returns array of TaxListInfoListing
   */
  getSelectedTaxInfo(): TaxListInfoListing[] {
    return this.taxListInfoListing.filter(
      (taxListInfo: TaxListInfoListing) =>
        taxListInfo.isPrimaryShareClass && taxListInfo.isChecked
    );
  }

  /**
   * Update state list in taxListInfoListing to reduce further call
   * @param checkedItems selected items
   * @returns boolean observable
   */
  private updateStateInfo$(
    checkedItems: TaxListInfoListing[]
  ): Observable<boolean> {
    const taxExemptStatesRecords: TaxListInfoListing[] = checkedItems.filter(
      (taxListInfo: TaxListInfoListing) =>
        taxListInfo.stateFlag &&
        taxListInfo.isChecked &&
        !taxListInfo.isTaxExemptAvailable
    );
    if (taxExemptStatesRecords) {
      const variables: TaxInfoStateParam[] = taxExemptStatesRecords.map(
        (record: TaxListInfoListing) => {
          return {
            fundId: record.fundId,
            shareClassCode: record.shareClassCode,
            year: Number(record.taxYear),
          };
        }
      );

      // Fetch all tax states for selected items
      return this.fetchTaxInfoStates$(variables).pipe(
        map((taxInfoStates: TaxInfoStateList[]) => {
          const taxInfoStateInfoList = [...taxInfoStates.values()] || [];
          taxInfoStateInfoList.forEach((state: TaxInfoStateList) => {
            const taxInfoItem = checkedItems.find(
              (taxListInfo: TaxListInfoListing) =>
                taxListInfo.fundId === state.fundId
            );
            if (taxInfoItem) {
              taxInfoItem.isTaxExemptAvailable = true;
              taxInfoItem.taxExemptData = state.states;
            }
          });
          return true;
        })
      );
    } else {
      return of(true);
    }
  }
}
